import { useCallback, useEffect, useRef } from 'react';
import { setAuthLastActivityTime } from '@utils/auth-utils';
import { getConfig } from '@utils/config-utils';

// Events to listen for checking if the user is idle.
const EVENTS = [
  'mousemove',
  'keydown',
  'wheel',
  'DOMMouseScroll',
  'mouseWheel',
  'mousedown',
  'touchstart',
  'touchmove',
  'MSPointerDown',
  'MSPointerMove'
];

/**
 * Custom hook to monitor if the user is idle.
 *
 * This is based on https://github.com/SupremeTechnopriest/react-idle-timer
 * I didn't use directly that 'react-idle-timer' package, since it's not
 * correctly packaged and causes problems with the React package (npm will see
 * two conflicting React versions and nothing will work).
 *
 * 'react-idle-timer' has some bugs (for example it doesn't set this.state.oldDate)
 * and has other functionality that we don't need, so this version is a stripped-down one,
 * it contains only what we need, and with bugs fixed.
 *
 * @param {Function} onIdle - Callback function called when the user becomes idle.
 * @returns {void}
 */
const useIdleMonitor = onIdle => {
  const timeoutIdRef = useRef(null);
  const oldDateRef = useRef(null);
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  const idleTimeout = getConfig().idleTimeout;

  const clearTimer = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
  }, []);

  const setTimer = useCallback(() => {
    oldDateRef.current = +new Date();
    timeoutIdRef.current = setTimeout(() => {
      if (onIdleRef.current) {
        onIdleRef.current();
      }
    }, idleTimeout);
  }, [idleTimeout]);

  const onEvent = useCallback(event => {
    // Throttle mousemove events:
    if (event.type === 'mousemove') {
      const elapsed = (+new Date()) - oldDateRef.current;
      if (elapsed < 10000) {
        return;
      }
    }

    // Clear the session expiration timer:
    setAuthLastActivityTime();

    // Clear any existing timer:
    clearTimer();

    // Set timer again:
    setTimer();
  }, [clearTimer, setTimer]);

  useEffect(() => {
    // Set initial timer
    setTimer();

    // Add event listeners
    EVENTS.forEach(event => document.addEventListener(event, onEvent));

    // Cleanup
    return () => {
      // Clear timeout to prevent delayed state changes:
      clearTimer();

      // Unbind all events:
      EVENTS.forEach(event => document.removeEventListener(event, onEvent));
    };
  }, [clearTimer, onEvent, setTimer]);
};

export default useIdleMonitor;
